import PropTypes from "prop-types";
import { Grid, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { Bar } from "react-chartjs-2";
import {
  CARRIER_COLORS,
  GraphicNoDataImg,
  MONTH_SHORT_LABELS,
} from "../constants/graphics";
import ChartPlaceholder from "./chartPlaceholder";
import {
  formatNumber,
  formatNumberShort,
} from "../../../helpers/functions/functions";

const CARRIERS = [
  { key: "MOVISTAR", label: "Movistar" },
  { key: "DIGITEL", label: "Digitel" },
  { key: "MOVILNET", label: "Movilnet" },
];

// Envío por operadora mes a mes: barras apiladas de Enero a Diciembre
const CarrierMonthlyBarChart = ({ darkMode, loading, months }) => {
  const hasData = months.some((month) => (month?.solicitados || 0) > 0);

  const barData = {
    labels: MONTH_SHORT_LABELS,
    datasets: CARRIERS.map(({ key, label }) => ({
      label,
      data: months.map(
        (month) => month?.totalServiceProvider?.[key]?.solicitados || 0,
      ),
      backgroundColor: CARRIER_COLORS[key],
      hoverBackgroundColor: CARRIER_COLORS[key],
      borderWidth: 0,
      borderSkipped: false,
      maxBarThickness: 32,
      categoryPercentage: 0.7,
      barPercentage: 0.9,
    })),
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      tooltip: {
        backgroundColor: !darkMode ? "white" : "#4E4E4E",
        bodyColor: !darkMode ? "#000" : "white",
        borderColor: "#1BC3DD",
        borderWidth: 1,
        titleColor: !darkMode ? "#000" : "white",
        callbacks: {
          label: (context) =>
            ` ${context.dataset.label}: ${formatNumber(context.parsed.y || 0)}`,
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: darkMode ? "#F8FAFC" : "#475569",
          font: { family: "Ubuntu", size: 12 },
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        border: { display: false },
        grid: {
          display: true,
          color: "rgba(224, 224, 224, 0.5)",
          borderDash: [5, 5],
        },
        ticks: {
          color: darkMode ? "#CBD5E1" : "#64748B",
          font: { family: "Ubuntu", size: 12 },
          maxTicksLimit: 6,
          callback: (value) => formatNumberShort(Number(value)),
        },
      },
    },
  };

  return (
    <Grid
      item
      container
      direction={"column"}
      sx={{
        width: "100%",
        height: "100%",
        minWidth: 0,
        // Sin wrap: con wrap la columna toma el ancho del canvas y no encoge al redimensionar
        flexWrap: "nowrap",
        padding: "20px",
        borderRadius: "10px",
        backgroundColor: darkMode ? "#000000DE" : "#FFFFFF",
        border:
          "1px solid " + (darkMode ? "rgba(224, 224, 224, 0.50)" : "#E0E0E0"),
        boxShadow: "0 2px 8px 0 rgba(0, 0, 0, 0.10)",
      }}
    >
      <Typography
        sx={{
          fontFamily: "Ubuntu !important",
          fontWeight: "500",
          fontSize: "18px",
          color: darkMode ? "white" : "black",
        }}
      >
        Envío por operadora mes a mes
      </Typography>

      {loading || !hasData ? (
        <ChartPlaceholder
          darkMode={darkMode}
          loading={loading}
          image={
            darkMode
              ? GraphicNoDataImg.NoDataLineDark
              : GraphicNoDataImg.NoDataLineWhite
          }
        />
      ) : (
        <>
          <Box
            sx={{
              flex: 1,
              position: "relative",
              width: "100%",
              minWidth: 0,
              minHeight: "320px",
              overflow: "hidden",
              marginTop: "16px",
            }}
          >
            <Bar data={barData} options={barOptions} />
          </Box>
          <Grid
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: "24px",
              flexShrink: 0,
              paddingTop: "12px",
            }}
          >
            {CARRIERS.map(({ key, label }) => (
              <Box
                key={key}
                sx={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <Box
                  sx={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    backgroundColor: CARRIER_COLORS[key],
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "Ubuntu !important",
                    fontSize: "12px",
                    color: darkMode ? "white" : "#000",
                  }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Grid>
        </>
      )}
    </Grid>
  );
};

CarrierMonthlyBarChart.propTypes = {
  darkMode: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  months: PropTypes.array.isRequired,
};

export default CarrierMonthlyBarChart;
